import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { formatDisplayName } from "@/hooks/useUserProfile";
import { generalReportException, reportLog, supabaseReportException } from "@/lib/logs/reportExceptions";

interface PrivateMessageNotificationsContextType {
  unreadCount: number;
  isLoading: boolean;
  markMessagesAsRead: (conversationId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const PrivateMessageNotificationsContext = createContext<PrivateMessageNotificationsContextType | undefined>(undefined);

export function PrivateMessageNotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { toast, dismiss } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Ref for stable callback
  const showMessageToastRef = useRef<((message: any) => Promise<void>) | null>(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      setIsLoading(false);

      return;
    }

    try {
      // Get total unread USER messages count where user is the receiver
      const { count, error } = await supabase
        .from("private_messages")
        .select("*", { count: "exact", head: true })
        .eq("receiver_id", user.id)
        .is("read_at", null)
        .not("sender_id", "is", null); // Only user messages (exclude system messages)

      if (error) throw error;

      const newCount = count || 0;
      setUnreadCount(newCount);
    } catch (error) {
      generalReportException(error as Error, { operation: "PrivateMessageNotificationsContext.fetchUnreadCount", additionalData: { user_id: user?.id } });
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const showMessageToast = useCallback(async (message: any) => {
    try {
      // Conversation bilgilerini fetch et
      const { data: conversation, error: convError } = await supabase
        .from("conversations")
        .select("id, project_id")
        .eq("id", message.conversation_id)
        .single();

      if (convError) {
        supabaseReportException(convError, { operation: "PrivateMessageNotificationsContext.showMessageToast", additionalData: { user_id: user?.id } });
        // Conversation fetch başarısız olsa bile toast gösterelim (güvenli taraf)
      }

      // Kullanıcının bulunduğu sayfayı kontrol et
      const currentPath = location.pathname;
      const offerPageMatch = currentPath.match(/^\/customer\/offers\/([^/]+)$/) || currentPath.match(/^\/expert\/offers\/([^/]+)$/);

      if (offerPageMatch && conversation?.project_id) {
        // Kullanıcı bir teklif detay sayfasında
        const currentOfferId = offerPageMatch[1];

        // Bu teklif hangi projeye ait?
        const { data: currentOffer, error: offerError } = await supabase
          .from("offers")
          .select("project_id")
          .eq("id", currentOfferId)
          .single();

        if (offerError) {
          supabaseReportException(offerError, { operation: "PrivateMessageNotificationsContext.showMessageToast", additionalData: { user_id: user?.id } });
        }

        // Eğer gelen mesajın projesi ile mevcut teklif sayfasının projesi aynıysa toast gösterme
        if (currentOffer?.project_id && currentOffer.project_id === conversation.project_id) {
          return; // Toast gösterme
        }
      }

      // Project bilgisini fetch et
      let projectTitle = "Bir proje";
      if (conversation?.project_id) {
        const { data: project } = await supabase
          .from("projects")
          .select("title")
          .eq("id", conversation.project_id)
          .single();

        if (project?.title) {
          projectTitle = project.title;
        }
      }

      // Sender bilgilerini fetch et
      const { data: sender } = await supabase
        .from("profiles")
        .select("display_name, username")
        .eq("user_id", message.sender_id)
        .single();

      // Format sender name as "FirstName L." using the utility function
      const senderProfile = {
        user_id: message.sender_id as string,
        display_name: sender?.display_name || null,
        username: sender?.username || null,
        avatar_url: null,
        city: null,
        specialties: null,
        phone: null,
        phone_verified: false,
        user_type: null,
      };
      const senderName = formatDisplayName(senderProfile);

      // Mesaj içeriğini kısalt (ilk 80 karakter)
      const messagePreview = message.message_content
        ? message.message_content.length > 80
          ? message.message_content.substring(0, 80) + "..."
          : message.message_content
        : message.message_type === "file"
          ? "📎 Dosya gönderildi"
          : message.message_type === "image"
            ? "📷 Resim gönderildi"
            : message.message_type === "video"
              ? "🎥 Video gönderildi"
              : "💬 Yeni mesaj";

      // Toast göster
      const conversationId = message.conversation_id;
      const toastInstance = toast({
        title: (
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            <span>{senderName} - {projectTitle}</span>
          </div>
        ) as any,
        description: messagePreview,
        variant: "success",
        duration: 5000,
        onClick: async () => {
          try {
            // 1. Conversation'dan project ve participant bilgilerini al
            const { data: conversation, error: conversationError } = await supabase
              .from("conversations")
              .select("project_id, participant_1_id, participant_2_id")
              .eq("id", conversationId)
              .single();

            if (conversationError) {
              supabaseReportException(conversationError, { operation: "PrivateMessageNotificationsContext.showMessageToast", additionalData: { user_id: user?.id } });
              dismiss(toastInstance);

              return;
            }

            // 2. User tipini belirle
            const userTypeFromAuth = (user?.user_metadata as any)?.user_type;
            const isUzman = userTypeFromAuth === "uzman";

            if (!user?.id) {
              dismiss(toastInstance);

              return;
            }

            // 3. Doğru offer ID'sini bul
            let offerId;

            if (isUzman) {
              // Uzman için: kendi verdiği teklifi bul
              const { data: offer } = await supabase
                .from("offers")
                .select("id")
                .eq("project_id", conversation.project_id)
                .eq("contractor_id", user.id)
                .order("created_at", { ascending: false })
                .limit(1)
                .single();

              offerId = offer?.id;
            } else {
              // İşveren için: diğer participant'ın (uzman) verdiği teklifi bul
              const expertId = conversation.participant_1_id === user?.id
                ? conversation.participant_2_id
                : conversation.participant_1_id;

              const { data: offer } = await supabase
                .from("offers")
                .select("id")
                .eq("project_id", conversation.project_id)
                .eq("contractor_id", expertId)
                .order("created_at", { ascending: false })
                .limit(1)
                .single();

              offerId = offer?.id;
            }

            // 4. Offer bulunamazsa fallback
            if (!offerId) {
              reportLog("Offer not found, fallback to messages page");
              const messagesRoute = isUzman
                ? `/expert/messages?conversation=${conversationId}`
                : `/messages?conversation=${conversationId}`;
              navigate(messagesRoute);
              dismiss(toastInstance);

              return;
            }

            // 5. Doğru teklif sayfasına yönlendir
            const route = isUzman
              ? `/expert/offers/${offerId}`
              : `/customer/offers/${offerId}`;

            navigate(route);

            // Toast'u kapat
            dismiss(toastInstance);

          } catch (error) {
            generalReportException(error as Error, { operation: "PrivateMessageNotificationsContext.showMessageToast", additionalData: { user_id: user?.id } });
            dismiss(toastInstance);
          }
        },
        className: "cursor-pointer hover:bg-accent",
      } as any);
    } catch (error) {
      generalReportException(error as Error, { operation: "PrivateMessageNotificationsContext.showMessageToast", additionalData: { user_id: user?.id } });
      toast({
        title: "Hata",
        description: "Mesaj gönderimi yapılamadı",
        variant: "error",
      });
    }
  }, [toast, navigate, user, location.pathname]);

  const markMessagesAsRead = useCallback(async (conversationId: string) => {
    if (!user) return;

    try {
      // Get count before marking as read for immediate UI update
      const { count: unreadInConversation } = await supabase
        .from("private_messages")
        .select("*", { count: "exact", head: true })
        .eq("conversation_id", conversationId)
        .eq("receiver_id", user.id)
        .is("read_at", null)
        .not("sender_id", "is", null); // Only user messages

      // Immediately update count for responsive UI
      if (unreadInConversation && unreadInConversation > 0) {
        setUnreadCount(prev => {
          const newCount = Math.max(0, prev - unreadInConversation);

          return newCount;
        });
      }

      const { error } = await supabase
        .from("private_messages")
        .update({ read_at: new Date().toISOString() })
        .eq("conversation_id", conversationId)
        .eq("receiver_id", user.id)
        .is("read_at", null);

      if (error) throw error;

      // Refresh count from server for accuracy
      await fetchUnreadCount();
    } catch (error) {
      generalReportException(error as Error, { operation: "PrivateMessageNotificationsContext.markMessagesAsRead", additionalData: { user_id: user?.id } });
      // Refresh count even on error
      await fetchUnreadCount();
    }
  }, [user?.id, fetchUnreadCount]);

  // Synchronously update ref on every render
  showMessageToastRef.current = showMessageToast;

  // Initial fetch on mount
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
    }
  }, [user?.id, fetchUnreadCount]);

  // Visibility and online handlers - refresh when user returns to tab or comes back online
  // This ensures data is fresh even if WebSocket connection became stale
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // User returned to tab - refresh data
        fetchUnreadCount();
      }
    };

    const handleOnline = () => {
      // Network came back online - refresh data
      fetchUnreadCount();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
    };
  }, [user?.id, fetchUnreadCount]);

  // Real-time subscriptions for message notifications
  useEffect(() => {
    if (!user) {
      return;
    }

    // Simple, stable channel name - no random suffix
    const channelName = `private-message-notifications-${user.id}`;

    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "private_messages",
          filter: `receiver_id=eq.${user.id}`,
        },
        async (payload) => {
          const newMessage = payload.new as any;

          // Only increment count for user messages (skip system messages)
          if (newMessage.sender_id) {
            setUnreadCount(prev => prev + 1);

            // Show toast notification using ref (always has latest function)
            if (showMessageToastRef.current) {
              await showMessageToastRef.current(newMessage);
            }
          } else {
            reportLog("Skipping system message (sender_id is null)");
          }
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "private_messages",
          filter: `receiver_id=eq.${user.id}`,
        },
        (payload) => {
          const newMessage = payload.new as any;
          const oldMessage = payload.old as any;

          // Only decrement count for user messages
          if (!oldMessage.read_at && newMessage.read_at && newMessage.sender_id) {
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
        },
      )
      .subscribe((status, err) => {
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
          // Connection lost - refresh data as fallback
          fetchUnreadCount();

          if (status !== "CLOSED") {
            generalReportException(new Error(`Subscription error: ${status}`), {
              operation: "PrivateMessageNotificationsContext.subscription",
              additionalData: {
                user_id: user.id,
                status,
                channelName,
                error: err,
              },
            });
          }
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]); // Only depend on user.id - no other dependencies

  return (
    <PrivateMessageNotificationsContext.Provider value={{
      unreadCount, isLoading, markMessagesAsRead, refresh: fetchUnreadCount,
    }}
    >
      {children}
    </PrivateMessageNotificationsContext.Provider>
  );
}

export function usePrivateMessageNotificationsContext() {
  const context = useContext(PrivateMessageNotificationsContext);
  if (context === undefined) {
    throw new Error("usePrivateMessageNotificationsContext must be used within a PrivateMessageNotificationsProvider");
  }

  return context;
}
